import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Dashboard.css';
import { toast } from 'react-toastify';
import BotDetails from './BotDetails';
import { FaHome, FaCogs, FaQuestionCircle, FaPhone, FaTicketAlt, FaBars, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import SupportWidget from "./SupportWidget";
import PlansBilling from './PlansBilling';
import { FiMoreVertical } from "react-icons/fi";
import HelpCenter from "./HelpCenter";
import ContactSales from "./ContactSales";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

/* ===================== PLAN GUARD: central specs ===================== */
const PLAN_SPECS = {
  none: {
    label: 'No Plan',
    botLimit: 0,
    uploadCharLimit: 0,          // characters
    msgLimit: 0,                 // messages/mo (used in your sidebar metric)
    modelAccess: [],
    agenticActions: 0,
    leadCapture: false,
    sitemapUrlLimit: 0
  },
  starter: {
    label: 'Starter',
    botLimit: 1,
    uploadCharLimit: 10_000_000,
    msgLimit: 200,
    modelAccess: ['gpt-4o-mini'],
    agenticActions: 3,
    leadCapture: true,
    sitemapUrlLimit: 5000
  },
  pro: {
    label: 'Professional',
    botLimit: 2,
    uploadCharLimit: 50_000_000,
    msgLimit: 2000,
    modelAccess: ['gpt-4o-mini', 'gpt-4', 'upcoming'],
    agenticActions: 5,
    leadCapture: true,
    sitemapUrlLimit: 5000
  },
  advanced: {
    label: 'Advanced',
    botLimit: 2,
    uploadCharLimit: 100_000_000,
    msgLimit: 5000,
    modelAccess: ['gpt-4o-mini', 'gpt-4', 'upcoming'],
    agenticActions: 10, // per bot (capable of up to 5 workflows x 10 actions in your copy)
    leadCapture: true,
    sitemapUrlLimit: Infinity
  },
  enterprise: {
    label: 'Enterprise',
    botLimit: Infinity,
    uploadCharLimit: Infinity,
    msgLimit: Infinity,
    modelAccess: ['all'],
    agenticActions: Infinity,
    leadCapture: true,
    sitemapUrlLimit: Infinity
  }
};


const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [bots, setBots] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [ticketOpen, setTicketOpen] = useState(false);

  const [totalSent, setTotalSent] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [selectedSection, setSelectedSection] = useState("Home");
  const [openMenuId, setOpenMenuId] = useState(null);

  // NEW: responsive sidebar states
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);   // mobile drawer
  const [isCollapsed, setIsCollapsed] = useState(false);       // tablet/desktop mini mode

  // ---- derive current plan + limits
  const currentPlanKey = user?.billing?.planId || "none";
  const currentPlan = PLAN_SPECS[currentPlanKey];
  const [routeLoading, setRouteLoading] = useState(false);
  const onKebabClick = (e, id) => {
    e.stopPropagation();
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    if (location.state?.section) {
      setSelectedSection(location.state.section);
    }
  }, [location.state]);

  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/analytics/total/${encodeURIComponent(user.email)}`);
        const data = await res.json();
        setTotalSent(data?.total_messages ?? 0);
      } catch {
        setTotalSent(0);
      }
    })();
  }, [user]);

  const messageLimit = user?.billing?.limits?.messagesPerMonth ?? 0;

  const remainingMessages =
    Number.isFinite(messageLimit)
      ? Math.max(0, messageLimit - totalSent)
      : '∞';

  const handleSubmitTicket = async ({ subject, message }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject,
          message,
          user_name: user?.name || "",
          user_email: user?.email || "",
          page_url: window.location.href,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Ticket submitted!");
      setTicketOpen(false);
    } catch {
      toast.error("Could not submit ticket");
    }
  };

  const [selectedBot, setSelectedBot] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setUser(data);
      });
  }, []);

  useEffect(() => {
    if (!user?.email) return;

    fetch(`${API_BASE_URL}/api/auth/get-bots-by-email/${encodeURIComponent(user.email)}`)
      .then((res) => res.json())
      .then((data) => {
        setBots(data);
      })
      .catch(() => {
        setBots([]);
      });
  }, [user]);

  useEffect(() => {
    const successMsg = sessionStorage.getItem("botSuccess");
    if (successMsg) {
      toast.success(successMsg);
      sessionStorage.removeItem("botSuccess");
    }
  }, []);

  const handleBuildChatBot = () => {
    navigate("/BuildChatBot", { state: { fromDashboard: true } });
  };

  const handleBotClick = (botId) => {
    setRouteLoading(true);
    // tiny delay so overlay paints before route switch
    requestAnimationFrame(() => {
      navigate(`/bot/${botId}`);
    });
  };

  useEffect(() => {
    if (!user?.email) return;
    (async () => {
      const res = await fetch(
        `${API_BASE_URL}/api/auth/analytics/unique-users/${encodeURIComponent(user.email)}`
      );
      const data = await res.json();
      setUniqueUsers(data.unique_users ?? 0);
    })();
  }, [user]);


  useEffect(() => {
    const close = () => setOpenMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // Close mobile drawer when resizing up
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768 && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isSidebarOpen]);

  // Prevent background scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = isSidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isSidebarOpen]);

  const deleteBot = async (bot) => {
    const ok = window.confirm(`Delete “${bot.botName || "this bot"}”?`);
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/bots/${bot._id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
      setBots((prev) => prev.filter((b) => b._id !== bot._id));
      toast.success("Bot deleted");
    } catch (err) {
      toast.error(err.message || "Could not delete bot");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ===================== PLAN-BASED GUARDS ===================== */
  const botCount = bots.length;
  const botLimit = user?.billing?.limits?.chatbots ?? 0;
  const canCreateBot =
    Number.isFinite(botLimit)
      ? botCount < botLimit
      : true;

  const uploadCharLimit = user?.billing?.limits?.uploadChars ?? 0;
  const usedUploadChars = user?.billing?.usage?.uploadChars ?? 0;
  const remainingUploadChars =
    Number.isFinite(uploadCharLimit)
      ? Math.max(0, uploadCharLimit - usedUploadChars)
      : '∞';

  const isWithinCharLimit =
    !Number.isFinite(uploadCharLimit) || usedUploadChars <= uploadCharLimit;

  const onCreateClick = () => {
    if (!canCreateBot) {
      const cap = Number.isFinite(currentPlan.botLimit) ? currentPlan.botLimit : 'unlimited';
      toast.info(`Your current plan (“${currentPlan.label}”) allows ${cap} bot${cap === 1 ? '' : 's'}. Upgrade to add more.`);
      setSelectedSection('Billing');
      return;
    }
    if (!isWithinCharLimit) {
      toast.error(`Upload character limit reached for “${currentPlan.label}”. Please remove data or upgrade your plan.`);
      setSelectedSection('Billing');
      return;
    }
    handleBuildChatBot();
  };

  const MenuItem = ({ icon: Icon, label, section }) => (
    <li
      className={selectedSection === section ? "active" : ""}
      onClick={() => {
        setSelectedSection(section);
        setIsSidebarOpen(false); // close drawer on mobile nav
      }}
      title={isCollapsed ? label : undefined}
    >
      <Icon className="icon" />
      <span className="x-menu-text">{label}</span>
    </li>
  );

  // Helpful display strings
  const charLimitDisplay = Number.isFinite(uploadCharLimit)
    ? `${(uploadCharLimit / 1_000_000).toFixed(0)}M`
    : '∞';

  const usedCharsDisplay = `${(usedUploadChars / 1000).toFixed(2)}K`;

const calculateCharsPerBot = (bot) => {
  const sumText = (arr, mapper) =>
    arr?.reduce((sum, item) => sum + mapper(item), 0) || 0;

  return (
    sumText(bot.fileTexts, f => f.text?.length || 0) +
    sumText(bot.knowledgeBase, k => k.text?.length || 0) +
    sumText(bot.sheets, s => s.text?.length || 0) +
    sumText(bot.faqs, q => (q.q?.length || 0) + (q.a?.length || 0))
  );
};

  const handleLogout = () => {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        navigate("/");
  };
  return (
    <div className={`x-dashboard ${isCollapsed ? 'is-collapsed' : ''}`}>
      {/* Top bar (visible on mobile) */}
      <div className="x-topbar">
        <button
          className="x-hamburger"
          aria-label="Open menu"
          onClick={() => setIsSidebarOpen(true)}
        >
          <FaBars />
        </button>
        <h1>XenaChat</h1>
        {/* spacer */}
        <div style={{ width: 36 }} />
      </div>

      {/* Sidebar */}
      <aside className={`x-sidebar ${isCollapsed ? 'collapsed' : ''} ${isSidebarOpen ? 'open' : ''}`}>
        <div className="x-sidebar-head">
          <h1 className="x-sidebar-title">XenaChat</h1>
          {/* Collapse toggle (hidden on mobile) */}
          <button
            className="x-collapse-btn"
            onClick={() => setIsCollapsed((v) => !v)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
          {/* Close drawer (mobile) */}
          <button
            className="x-drawer-close"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <ul className="x-menu">
          <MenuItem icon={FaHome} label="Home" section="Home" />
          <MenuItem icon={FaCogs} label="API & Integrations" section="API" />
          <MenuItem icon={FaQuestionCircle} label="Help Center" section="Help" />
          <MenuItem icon={FaPhone} label="Contact Sales" section="Sales" />
          <li onClick={() => { setTicketOpen(true); setIsSidebarOpen(false); }}>
            <FaTicketAlt className="icon" />
            <span className="x-menu-text">Submit Ticket</span>
          </li>
        </ul>

        <div className="x-plan-box">
          <div className="x-plan-top">
            <span className="x-plan-badge">{currentPlan.label}</span>
            <button
              className="x-plan-link"
              onClick={() => { setSelectedSection("Billing"); setIsSidebarOpen(false); }}
            >
              Manage plan
            </button>
          </div>

          <div className="x-plan-metrics">
            <div className="x-plan-metric">
              <div className="x-plan-label">Uploaded Characters</div>
              <div className="x-plan-value">
                {(usedUploadChars / 1000).toFixed(2)}K /
                {Number.isFinite(uploadCharLimit)
                  ? (uploadCharLimit / 1_000_000).toFixed(0) + "M"
                  : "∞"}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.7 }}>
                Remaining: {Number.isFinite(uploadCharLimit)
                  ? (remainingUploadChars / 1000).toFixed(2) + "K"
                  : "∞"}
              </div>
            </div>

            <div className="x-plan-metric">
              <div className="x-plan-label">Remaining Messages</div>
              <div className="x-plan-value">{remainingMessages}</div>
            </div>
          </div>

          <button className="x-upgrade-btn" onClick={() => setSelectedSection("Billing")}>⚡ Upgrade</button>
          <button className='x-logout-btn' onClick={() => handleLogout()}>Logout</button>
        </div>
      </aside>

      {/* Mobile dim/overlay */}
      <div className={`x-dim ${isSidebarOpen ? 'show' : ''}`} onClick={() => setIsSidebarOpen(false)} />

      {/* Main */}
      <main className="x-main">
        {selectedSection === "Home" && (
          <>
            <header className="x-header"></header>

            <div className="x-create-card">
              <div>
                <h3>Create new bot</h3>
                <p>Check out our detailed docs to learn how to format your data and create the perfect bot for your needs</p>
                <button
                  onClick={onCreateClick}
                  disabled={!canCreateBot || !isWithinCharLimit}
                  aria-disabled={!canCreateBot || !isWithinCharLimit}
                  title={
                    !canCreateBot
                      ? `Your plan (${currentPlan.label}) allows ${Number.isFinite(currentPlan.botLimit) ? currentPlan.botLimit : 'unlimited'
                      } bot(s)`
                      : (!isWithinCharLimit ? `Upload character limit reached for ${currentPlan.label}` : undefined)
                  }
                  style={{ opacity: (!canCreateBot || !isWithinCharLimit) ? 0.6 : 1, cursor: (!canCreateBot || !isWithinCharLimit) ? 'not-allowed' : 'pointer' }}
                >
                  {canCreateBot && isWithinCharLimit ? 'Create Bot' : 'Upgrade to Create'}
                </button>
              </div>
            </div>

            <section className="x-bots-section">
              <h3>Your Bots</h3>
              <div className="x-bot-list">
                {bots.map((bot) => (
                  <div
                    className="x-bot-card"
                    key={bot._id}
                    onClick={() => handleBotClick(bot._id)}
                  >
                    <button
                      className="x-bot-kebab"
                      aria-label="Open menu"
                      onClick={(e) => onKebabClick(e, bot._id)}
                    >
                      <FiMoreVertical />
                    </button>

                    {openMenuId === bot._id && (
                      <div className="x-bot-menu" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="x-bot-menu-item danger"
                          onClick={() => deleteBot(bot)}
                        >
                          Delete bot
                        </button>
                      </div>
                    )}

                    <img
                      src={bot.botAvatarURL || "/default-bot.png"}
                      alt={bot.botName || "Bot Avatar"}
                      loading="lazy"
                    />
                    <div className="x-bot-details">
                      <h4>{bot.botName || "Unnamed Bot"}</h4>
                      <p>{bot.website || "No website"}</p>
                      <p style={{ fontSize: "12px", opacity: 0.7 }}>
                        Total Chars used by Bot <b>{bot.botName}:{" "}</b>
                        {(calculateCharsPerBot(bot) / 1000).toFixed(2)}K
                      </p>
                      <span className="x-bot-label">General bot</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="x-stats">
              <div className="x-stat-card">
                <h4>Usage</h4>
                <p>
                  Characters used: <strong>{`${usedCharsDisplay} / ${charLimitDisplay}`}</strong>
                </p>
                <p>
                  {Number.isFinite(botLimit)
                    ? <strong>{botCount} / {botLimit}</strong>
                    : <strong>{botCount}</strong>}{" "}
                  bots used
                </p>
              </div>
              <div className="x-stat-card">
                <h4>Inbox</h4>
                <p>Users: <strong>{uniqueUsers}</strong></p>
                <p>Conversations: <span>{totalSent}</span></p>
              </div>
              <div className="x-stat-card">
                <h4>ROI</h4>
                <p>Money saved: $0</p>
                <p>Time saved: 0s</p>
              </div>
            </section>
          </>
        )}

        {selectedSection === "API" && (
          <div><h2>API & Integrations</h2><p>Coming soon...</p></div>
        )}

        {selectedSection === "Help" && (
          <HelpCenter />
        )}

        {selectedSection === "Sales" && (
          <ContactSales />
        )}

        {selectedSection === "Billing" && (
          <PlansBilling
            currentPlan={{
              planId: user?.billing?.planId || "none",
              chatbotsUsed: bots.length,
              chatbotLimit: user?.billing?.limits?.chatbots || 0,
              charsUsed: user?.billing?.usage?.uploadChars || 0,
              charLimit: user?.billing?.limits?.uploadChars || 0,
              email: user?.email,
            }}
          />
        )}
      </main>

      <SupportWidget
        open={ticketOpen}
        onClose={() => setTicketOpen(false)}
        onSubmit={handleSubmitTicket}
      />
      {routeLoading && (
        <div className="route-loading-overlay">
          <div className="route-spinner" />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
